import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	api_deleteBooking,
	api_getBookings,
	api_getBookingsByFilter,
} from '../../../api/catering/booking'; // Adjusted import for Booking APIs
import Input from '../../../components/bootstrap/forms/Input';
import Icon from '../../../components/icon/Icon';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import SubHeader, {
	SubHeaderLeft,
	SubHeaderRight,
	SubheaderSeparator,
} from '../../../layout/SubHeader/SubHeader';
import Card, {
	CardActions,
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../../components/bootstrap/Card';
import Button from '../../../components/bootstrap/Button';
const PAGE_SIZE = 10;
const Item = ({ item, removeBookingFromTable, index }: any) => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const formatDateTime = (dateTime: Date) => {
		const date = new Date(dateTime);
		const options = {
			year: 'numeric' as const,
			month: 'short' as const,
			day: 'numeric' as const,
			hour: 'numeric' as const,
			minute: 'numeric' as const,
		};
		return date.toLocaleDateString('en-US', options);
	};

	return (
		<tr key={index}>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>{formatDateTime(item?.dateTime)}</div>
					</div>
				</div>
			</td>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>
							{item?.expectedGuests.from} - {item?.expectedGuests.to}
						</div>{' '}
						{/* Display event type name */}
					</div>
				</div>
			</td>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>{item?.user?.name}</div>{' '}
						{/* Display event type name */}
					</div>
				</div>
			</td>
			<td>
				<div className='d-flex'>
					<div className='flex-grow-1'>
						<div className='fs-6 fw-bold'>{item?.status}</div>{' '}
						{/* Display event type name */}
					</div>
				</div>
			</td>
			<td>
				<span
					onClick={() => navigate(`/edit/booking`, { state: item })} // Navigate to edit event type route
					style={{
						margin: 10,
						cursor: 'pointer',
						display: item?.status === 'pending' ? 'inline' : 'none',
					}}>
					EDIT
				</span>
			</td>
		</tr>
	);
};

const AllBooking = () => {
	const [bookings, setBookings] = useState<any>([]); // State variable to hold event types
	const [loading, setLoading] = useState(false);
	const [pageNo, setPageNo] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const navigate = useNavigate();
	const tableBodyRef = useRef<HTMLTableSectionElement>(null);
	const [filter, setFilter] = useState(false);
	const [searchString, setSearchString] = useState('');

	// Function to fetch event types
	const getBookings = async (page_no?: number) => {
		const finalPageNo = page_no || pageNo;

		try {
			setLoading(true);
			const res = await api_getBookings(pageNo, PAGE_SIZE);
			if (res.isSuccess) {
				if (!res.data.length) {
					setHasMore(false);
				} else {
					if (page_no === 1) {
						setBookings(res.data);
						setPageNo(2);
					} else {
						setBookings([...bookings, ...res.data]);
						setPageNo(pageNo + 1);
					}
				}
			} else {
				console.error('Failed to fetch event types:', res.message);
			}
		} catch (error) {
			console.error('Error fetching event types:', error);
		} finally {
			setLoading(false);
		}
	};
	const getBookingByFilter = async () => {
		try {
			setLoading(true);
			const res = await api_getBookingsByFilter(searchString);
			if (res.isSuccess) {
				if (!res.data.length) {
					setHasMore(false);
					return;
				}
				setBookings(res.data);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	// Function to remove event type from table
	const removeBookingFromTable = (bookingId: string) => {
		setBookings(bookings.filter((e: any) => e._id !== bookingId));
	};

	// Load more event types
	const onLoadMore = () => {
		if (hasMore) {
			getBookings();
		}
	};

	// Handle filter reset
	const handleFilterReset = () => {
		setBookings([]);
		setHasMore(true);
		getBookings(1);
	};

	// Fetch event types on component mount
	useEffect(() => {
		getBookings();
	}, []);

	// Add scroll event listener to table body
	useEffect(() => {
		tableBodyRef.current?.addEventListener('scroll', (e) => {
			console.log(e);
		});
	}, []);

	return (
		<PageWrapper title={'Booking'}>
			<div style={{ marginTop: '20px' }}>
				<SubHeader>
					<SubHeaderLeft>
						<label
							className='border-0 bg-transparent cursor-pointer me-0'
							htmlFor='searchInput'>
							<Icon
								icon='Search'
								size='2x'
								color='primary'
								onClick={() => {
									setBookings([]);
									setHasMore(true);
									getBookingByFilter();
								}}
							/>
						</label>
						<Input
							value={searchString}
							onChange={(e: any) => setSearchString(e?.target?.value)}
							id='searchInput'
							type='search'
							className='border-0 shadow-none bg-transparent'
							placeholder='Search booking...'
						/>
					</SubHeaderLeft>
				</SubHeader>
			</div>
			<Card stretch>
				<CardHeader borderSize={1}>
					<CardLabel icon='BookOnline' iconColor='info'>
						<CardTitle>Bookings</CardTitle>
					</CardLabel>
				</CardHeader>
				<CardBody isScrollable className='table-responsive'>
					<table className='table table-modern'>
						<thead>
							<tr>
								<th>Date/Time</th>
								<th>Guest #</th>
								<th>Customer</th>
								<th>Status</th>
								<th>Action</th>
							</tr>
						</thead>
						<tbody>
							{bookings?.map((item: any, index: any) => (
								<Item
									key={index}
									item={item}
									index={index}
									removeBookingFromTable={removeBookingFromTable}
								/>
							))}
						</tbody>
					</table>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							marginBottom: '20px',
						}}>
						{hasMore && (
							<button
								style={{
									border: 'none',
									padding: '10px',
									borderRadius: '10px',
									cursor: 'pointer',
								}}
								onClick={onLoadMore}>
								Load More...
							</button>
						)}
					</div>
				</CardBody>
			</Card>
		</PageWrapper>
	);
};

export default AllBooking;
